import { useRef, useState, useEffect } from "react";
import { useApi } from "../../hooks/use-api";
import { useSingleton } from "../../hooks/use-singleton";
import {
  API_PARTS_TNPSC,
  API_AI_CONTENT_LESSON,
  API_RANDOM_MULTI_TNPSC,
  apiMyAssestsTnpscSearch,
  API_UNIT_TN,
  API_SHORT_MULTI_TN,
  API_LONG_MULTI_TN,
  API_CHPATER_TN,
  apiStudentStudys,
  API_MEDIUM_TNPSC,
  API_TNPSC_AI_CONCEPTS_DROPDOWN,
  API_GENERATE_VIDEO_TNPSC,
} from "../../utils/api-routes";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../hooks/use-notify";
import { ROUTE_LEARNING_ASSETS } from "../../route/tnpscRoute";

const useHandler = () => {
  const { get, post } = useApi();
  const { toggleNotify } = useNotify();
  const { mail, user, role, medium } = useSingleton();
  const navigate = useNavigate();
  const DURATION_OPTIONS = ["Secs", "Mins", "Hrs"];
  const [filteredAssets, setFilteredAssets] = useState([]);
  const [assetPopupOpen, setAssetPopupOpen] = useState(false);

  /* ===================== UI STATES ===================== */
  const [togglebtn, setToggleBtn] = useState(false);
  const [popupData, setPopupData] = useState({
    open: false,
    title: "",
    description: "",
    buttonText: "OK",
    assetId: null,
  });
  const [openDropdown, setOpenDropdown] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [selectedMedium, setSelectedMedium] = useState("");
  const [conceptText, setConceptText] = useState({});
  const [preferredType, setPreferredType] = useState("audio");
  const [selectMode, setSelectMode] = useState("random");
  const [isLoading, setIsLoading] = useState(false);
  const [classdetails, setClassDetails] = useState({});
  const [selectedPart, setSelectedPart] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("");
  const [duration, setDuration] = useState("");
  const [units, setUnits] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [selectedLesson, setSelectedLesson] = useState("");
  const languageRef = useRef(null);
  const subjectRef = useRef(null);
  const chapterRef = useRef(null);
  const mediumRef = useRef(null);
  const [durationType, setDurationType] = useState("Mins");
  const [durationDropdown, setDurationDropdown] = useState(false);
  const [conceptOptions, setConceptOptions] = useState([]);

  const partRef = useRef(null);
  const unitRef = useRef(null);
  const lessonRef = useRef(null);

  /* ===================== DATA STATES ===================== */

  // 👉 ALL SUBJECT OPTIONS FROM API
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [chapterOptions, setChapterOptions] = useState({});
  const [mediums, setMediums] = useState([]);

  // 👉 USER SELECTED SUBJECTS (UI ROWS)
  const [subjects, setSubjects] = useState([
    {
      id: 1,
      name: "",
      units: [],
      chapters: [],
      selectedChapterId: null,
    },
  ]);
  // console.log(subjects, "jhsubjects");
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedChapter, setSelectedChapter] = useState("");

  /* ===================== API CALLS ===================== */

  const filteredDurationOptions =
    role === "guest" ? ["Secs"] : DURATION_OPTIONS;

  useEffect(() => {
    if (role === "guest") {
      setDurationType("Secs");
    }
  }, [role]);

  const handleDurationChange = (val) => {
    if (role === "guest") {
      if (parseInt(val) > 30) {
        setDuration("30");
        return;
      }
    }
    setDuration(val);
  };

  const getUserStudiesDetails = async () => {
    try {
      const res = await get(apiStudentStudys(mail));
      if (res?.data?.data) {
        setClassDetails(res?.data);
        const payload = {
          board: res.data.data.board,
          clas: res.data.data.class,
        };
        getMediumDetails(payload);
      }
    } catch (e) {
      console.log("Error getting study details", e);
    }
  };

  const getMediumDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_MEDIUM_TNPSC, payload);
      if (res) {
        setMediums(res?.data?.mediums);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting Subject Details", e);
    }
  };

  const getPartDetails = async (payload) => {
    try {
      const res = await post(API_PARTS_TNPSC, payload);
      if (res?.data?.parts) {
        setSubjectOptions(res?.data?.parts); // ✅ OPTIONS ONLY
        console.log(res?.data?.parts, "res");
      }
    } catch (e) {
      console.log("Error getting subjects", e);
    }
  };

  const getChapterDetails = async (payload) => {
    try {
      const res = await post(API_CHPATER_TN, payload);

      if (res?.data?.chapters) {
        setChapters(res.data.chapters); // ✅ store as simple array
      }
    } catch (e) {
      console.log("Error getting chapters", e);
    }
  };

  const getLessonDetails = async (payload) => {
    try {
      const res = await post(API_AI_CONTENT_LESSON, payload);

      if (res?.data?.lessons) {
        setLessons(res.data.lessons);
      }
    } catch (e) {
      console.log("Error getting lessons", e);
    }
  };

  useEffect(() => {
    if (!selectedUnit) return;

    const payload = {
      board: user?.data?.board,
      clas: user?.data?.class,
      medium: medium,
      part: selectedPart,
      unit: selectedUnit,
    };

    getChapterDetails(payload);
  }, [selectedUnit]);

  useEffect(() => {
    if (!selectedSubject) return;
    const payload = {
      board: user?.data?.board,
      clas: user?.data?.class,
      medium: medium,
      part: selectedPart,
      unit: selectedUnit,
      chapter: selectedSubject,
    };
    getLessonDetails(payload);
  }, [selectedSubject]);

  const getUnitsDetails = async (payload) => {
    setIsLoading(true);
    try {
      const res = await post(API_UNIT_TN, payload);
      if (res) {
        setUnits(res?.data?.units);
        setIsLoading(false);
      } else {
        alert(res?.message);
      }
    } catch (e) {
      setIsLoading(false);
      console.log("Error on getting Subject Details", e);
    }
  };

  useEffect(() => {
    console.log("Subjects changed:", subjects);
  }, [subjects]);

  useEffect(() => {
    getUserStudiesDetails();
  }, []);

  const formattedDetails = () => {
    const formattedParts = subjects?.reduce((acc, subject) => {
      if (!subject?.name) return acc;

      const partName = subject.name;
      const subjectObj = {}; // ✅ NEW LEVEL (Indian polity)

      subject.units?.forEach((unit) => {
        if (!unit?.name) return;

        const subjectName = unit.name; // ⚠️ this is actually SUBJECT
        subjectObj[subjectName] = {};

        unit.chapters?.forEach((chapter) => {
          if (!chapter?.name) return;

          const unitName = chapter.name; // ⚠️ this is UNIT
          subjectObj[subjectName][unitName] = {};

          const lessonName = Array.isArray(chapter.lesson)
            ? chapter.lesson[0]
            : chapter.lesson;

          const conceptsArray = (chapter.concepts || []).filter(
            (c) => c && c.trim() !== "",
          );

          if (lessonName) {
            subjectObj[subjectName][unitName][lessonName] = conceptsArray;
          }

          // ❌ remove empty unit
          if (Object.keys(subjectObj[subjectName][unitName]).length === 0) {
            delete subjectObj[subjectName][unitName];
          }
        });

        // ❌ remove empty subject
        if (Object.keys(subjectObj[subjectName]).length === 0) {
          delete subjectObj[subjectName];
        }
      });

      if (Object.keys(subjectObj).length > 0) {
        acc[partName] = subjectObj;
      }

      return acc;
    }, {});

    return {
      mail,
      board: user?.data?.board,
      clas: user?.data?.class,
      medium: medium,
      language: selectedLanguage,
      parts: formattedParts,
      video: false,
      audio: true,
    };
  };

  const onSelectLesson = ({ subjectId, chapterId, lessonName }) => {
    setSubjects((prev) =>
      prev.map((subject) =>
        subject.id === subjectId
          ? {
              ...subject,
              units: subject.units.map((unit) => ({
                ...unit,
                chapters: unit.chapters.map((ch) => {
                  if (ch.id !== chapterId) return ch;

                  const existing = ch.lessons || [];

                  // ✅ LIMIT ONLY FOR LONG MODE
                  if (selectMode === "long" && existing.length >= 2) {
                    return ch; // stop adding
                  }

                  // ✅ prevent duplicate
                  if (existing.includes(lessonName)) {
                    return ch;
                  }

                  return {
                    ...ch,
                    lessons: [...existing, lessonName],
                  };
                }),
              })),
            }
          : subject,
      ),
    );
  };
  const generateAIContent = async () => {
    const payload = formattedDetails();
    try {
      const res = await post(API_SHORT_MULTI_TN, payload);
      if (res) {
        if (res?.data?.status === "ALREADY_EXISTS") {
          const assetId = res?.data?.existing_assets?.[0]?.asset_id;

          setPopupData({
            open: true,
            title: "Already Exists",
            description: res?.data?.message || "Content already generated",
            buttonText: "OK",
            assetId: assetId,
          });
        } else {
          setPopupData({
            open: true,
            title: "Hang Tight!",
            description:
              "We’re working on your content right now. You don’t have to stay on this page — we’ll send you an email once it's ready.",
            buttonText: "Go to Assets Page",
          });
        }
      }
    } catch (e) {
      console.log("ERROR IS COMES FROM GENEREATEAICONTENT API", e);
    }
  };

  const aiGenerate = () => generateAIContent();

  const formattedLongDetails = () => {
    const formattedSubjects = subjects?.reduce((acc, subject) => {
      if (!subject?.name) return acc;

      const partName = subject.name;
      const subjectObj = {}; // ✅ subject level

      subject.units?.forEach((unit) => {
        if (!unit?.name) return;

        const subjectName = unit.name; // ✅ Indian polity
        subjectObj[subjectName] = {};

        unit.chapters?.forEach((chapter) => {
          if (!chapter?.name) return;

          const unitName = chapter.name; // ✅ Unit-1

          const lessonsArray = (chapter.lessons || [])
            .flat()
            .filter((l) => typeof l === "string" && l.trim() !== "");

          if (!lessonsArray.length) return;

          // ✅ correct format (NO lessons key)
          subjectObj[subjectName][unitName] = lessonsArray;
        });

        // ❌ remove empty subject
        if (Object.keys(subjectObj[subjectName]).length === 0) {
          delete subjectObj[subjectName];
        }
      });

      if (Object.keys(subjectObj).length > 0) {
        acc[partName] = subjectObj;
      }

      return acc;
    }, {});

    return {
      mail,
      board: user?.data?.board,
      medium: medium,
      clas: user?.data?.class,
      language: selectedLanguage,
      parts: formattedSubjects,
      video: false,
      audio: true,
    };
  };

  const myAssetsApiWithFilters = async (currentFilters) => {
    try {
      const res = await get(
        apiMyAssestsTnpscSearch(mail, currentFilters.search),
      );

      return res; // ✅ IMPORTANT
    } catch (e) {
      console.log("ERROR", e);
    }
  };

  const handleExistingAssets = async (assetId) => {
    try {
      const res = await myAssetsApiWithFilters({ search: assetId });

      const assets = res?.data?.assets || [];
      setFilteredAssets(assets);

      return assets; // ✅ return
    } catch (e) {
      console.log("Error fetching assets", e);
    }
  };

  const generateAILongContent = async () => {
    const payload = formattedLongDetails();

    try {
      const res = await post(API_LONG_MULTI_TN, payload);

      if (res) {
        if (res?.data?.status === "ALREADY_EXISTS") {
          const assetId = res?.data?.existing_assets?.[0]?.asset_id; // ✅ FIX
          console.log(assetId, "asetid2");
          setPopupData({
            open: true,
            title: "Already Exists",
            description: res?.data?.message || "Content already generated",
            buttonText: "OK",
            assetId: assetId,
          });
        } else {
          setPopupData({
            open: true,
            title: "Hang Tight!",
            description:
              "We’re working on your content right now. You don’t have to stay on this page — we’ll send you an email as soon as it’s done. In the meantime, check out your previously generated assets",
            buttonText: "Go to Assets Page",
          });
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  const aiLongGenerate = () => generateAILongContent();

  const formattedRandomDetails = () => {
    const subject = subjects?.[0]; // only 1 part
    const unit = subject?.units?.[0]; // only 1 unit
    const chapter = unit?.chapters?.[0]; // only 1 chapter

    return {
      mail,
      board: user?.data?.board,
      clas: user?.data?.class,
      medium: medium,
      language: selectedLanguage,

      // ✅ REQUIRED FIELDS
      part: subject?.name || "",
      unit: unit?.name || "",
      chapter: chapter?.name || "",
      duration: `${duration} ${durationType}`,
      video: false,
      audio: true,
    };
  };

  const generateAIRandomContent = async () => {
    const payload = formattedRandomDetails();

    try {
      const res = await post(API_RANDOM_MULTI_TNPSC, payload);
      if (res) {
        if (res?.data?.status === "ALREADY_EXISTS") {
          const assetId = res?.data?.existing_assets?.[0]?.asset_id;

          setPopupData({
            open: true,
            title: "Already Exists",
            description: res?.data?.message || "Content already generated",
            buttonText: "OK",
            assetId: assetId,
          });
        } else {
          setPopupData({
            open: true,
            title: "Hang Tight!",
            description:
              "We’re generating your custom content. You can leave this page — we’ll notify you once it's ready.",
            buttonText: "Go to Assets Page",
          });
        }
      }
    } catch (e) {
      console.log("ERROR RANDOM API", e);
    }
  };

  const aiRandomGenerate = () => generateAIRandomContent();

  /* ===================== SUBJECT ACTIONS ===================== */

  const addSubject = () => {
    // Only one part allowed
    if (subjects.length >= 1) return;

    setSubjects([
      {
        id: 1,
        name: "",
        units: [],
        chapters: [],
        selectedChapterId: null,
      },
    ]);
  };

  const deleteSubject = (id) => {
    setSubjects((prev) => prev.filter((s) => s.id !== id));
  };

  const addChapters = (subjectId, unitId) => {
    setSubjects((prev) =>
      prev.map((sub) =>
        sub.id === subjectId
          ? {
              ...sub,
              units: sub.units.map((u) =>
                u.id === unitId
                  ? {
                      ...u,
                      chapters: [
                        ...u.chapters,
                        {
                          id: Date.now(),
                          name: "",
                          lesson: [],
                          concepts: [], // ✅ add
                        },
                      ],
                    }
                  : u,
              ),
            }
          : sub,
      ),
    );
  };

  // const deleteChapter = (subjectId, chapterId) => {
  //   setSubjects((prev) =>
  //     prev.map((s) =>
  //       s.id === subjectId
  //         ? {
  //             ...s,
  //             chapters: s.chapters.filter((c) => c.id !== chapterId),
  //             selectedChapterId:
  //               s.selectedChapterId === chapterId ? null : s.selectedChapterId,
  //           }
  //         : s,
  //     ),
  //   );
  // };

  const deleteChapter = (subjectId, unitId, chapterId) => {
    setSubjects((prev) =>
      prev.map((s) =>
        s.id === subjectId
          ? {
              ...s,
              units: s.units.map((u) =>
                u.id === unitId
                  ? {
                      ...u,
                      chapters: u.chapters.filter((c) => c.id !== chapterId),
                    }
                  : u,
              ),
            }
          : s,
      ),
    );
  };

  const getConceptDetails = async (payload) => {
    try {
      const res = await post(API_TNPSC_AI_CONCEPTS_DROPDOWN, payload);

      if (res?.data?.concepts) {
        setConceptOptions(res.data.concepts);
      }
    } catch (e) {
      console.log("Error getting concepts", e);
    }
  };

  const addConcept = (subjectId, unitId, chapterId, concept) => {
    if (!concept?.trim()) return;

    setSubjects((prev) =>
      prev.map((s) =>
        s.id === subjectId
          ? {
              ...s,
              units: s.units.map((u) =>
                u.id === unitId
                  ? {
                      ...u,
                      chapters: u.chapters.map((c) => {
                        if (c.id !== chapterId) return c;

                        const existing = c.concepts || [];

                        // ✅ prevent duplicate
                        if (existing.includes(concept)) return c;

                        // ✅ limit 2
                        if (existing.length >= 2) return c;

                        if (role === "guest") {
                          return {
                            ...c,
                            concepts: [...existing, concept.trim()],
                          };
                        }
                        if (existing.length >= 3) return c;

                        return {
                          ...c,
                          concepts: [...existing, concept.trim()],
                        };
                      }),
                    }
                  : u,
              ),
            }
          : s,
      ),
    );
  };

  const deleteConcept = (subjectId, unitId, chapterId, index) => {
    setSubjects((prev) =>
      prev.map((s) =>
        s.id === subjectId
          ? {
              ...s,
              units: s.units.map((u) =>
                u.id === unitId
                  ? {
                      ...u,
                      chapters: u.chapters.map((c) =>
                        c.id === chapterId
                          ? {
                              ...c,
                              concepts: c.concepts.filter(
                                (_, i) => i !== index,
                              ),
                            }
                          : c,
                      ),
                    }
                  : u,
              ),
            }
          : s,
      ),
    );
  };

  /* ===================== UTILS ===================== */

  // useEffect(() => {
  //   const handleClickOutside = (e) => {
  //     if (languageRef.current?.contains(e.target)) return;
  //     setOpenDropdown(null);
  //   };

  //   document.addEventListener("mousedown", handleClickOutside);
  //   return () => document.removeEventListener("mousedown", handleClickOutside);
  // }, []);

  const onToggleBtn = () => setToggleBtn((p) => !p);

  const onSelectSubject = ({ subjectId, subjectName }) => {
    setSelectedPart(subjectName); // ✅ store selected part

    setSubjects((prev) =>
      prev.map((s) =>
        s.id === subjectId ? { ...s, name: subjectName, units: [] } : s,
      ),
    );

    setOpenDropdown(null);
  };

  const onSelectChapter = ({ subjectId, chapterName }) => {
    setSubjects((prev) =>
      prev.map((s) => {
        if (s.id !== subjectId) return s;

        // ❌ Prevent duplicate
        if (s.chapters.some((c) => c.name === chapterName)) {
          return s;
        }

        if (s.chapters.length >= 2) return s;

        return {
          ...s,
          chapters: [
            ...s.chapters,
            {
              id: Date.now(),
              name: chapterName,
              concepts: [],
            },
          ],
        };
      }),
    );

    setOpenDropdown(null);
  };

  useEffect(() => {
    if (!medium || !classdetails?.data?.class) return;
    const payload = {
      board: classdetails?.data?.board,
      clas: classdetails?.data?.class,
      medium: medium,
    };

    getPartDetails(payload);
  }, [medium, classdetails?.data?.class]);

  useEffect(() => {
    if (!selectedPart || !user?.data?.class) return;
    const payload = {
      board: user?.data?.board,
      clas: user?.data?.class,
      medium: medium,
      part: selectedPart,
    };

    getUnitsDetails(payload);
  }, [selectedPart]);

  const addUnit = (subjectId) => {
    setSubjects((prev) =>
      prev.map((s) => {
        if (s.id !== subjectId) return s;

        if (s.units.length >= 3) return s; // ✅ max 3

        return {
          ...s,
          units: [
            ...s.units,
            {
              id: Date.now(),
              name: "",
              chapters: [],
            },
          ],
        };
      }),
    );
  };

  const deleteUnit = (subjectId, unitId) => {
    setSubjects((prev) =>
      prev.map((s) =>
        s.id === subjectId
          ? {
              ...s,
              units: s.units.filter((u) => u.id !== unitId),
            }
          : s,
      ),
    );
  };

  const resetForm = () => {
    setSubjects([
      {
        id: 1,
        name: "",
        unit: [],
        chapters: [],
        selectedChapterId: null,
      },
    ]);

    setSubjectOptions([]);
    setChapterOptions({});
    setSelectedLanguage("");
    setSelectedUnit("");
    setSelectedPart("");
    setSelectedSubject("");
    setSelectedLesson("");
  };

  const isFormValid = () => {
    // ================= COMMON =================
    if (!user?.data?.medium || !selectedLanguage) return false;

    const subject = subjects?.[0]; // only 1 part allowed
    if (!subject?.name) return false;

    // ================= SHORT MODE =================
    if (selectMode === "short") {
      return (
        subject.units?.length > 0 &&
        subject.units.every(
          (unit) =>
            unit.name &&
            unit.chapters?.length > 0 &&
            unit.chapters.every((chapter) => {
              const lesson = Array.isArray(chapter.lesson)
                ? chapter.lesson[0]
                : chapter.lesson;

              return chapter.name && lesson && chapter.concepts?.length > 0;
            }),
        )
      );
    }

    // ================= LONG MODE =================
    if (selectMode === "long") {
      return (
        subject.units?.length > 0 &&
        subject.units.every(
          (unit) =>
            unit.name &&
            unit.chapters?.length > 0 &&
            unit.chapters.every(
              (chapter) =>
                chapter.name && chapter.lessons && chapter.lessons.length > 0,
            ),
        )
      );
    }

    // ================= RANDOM MODE =================
    if (selectMode === "random") {
      const unit = subject.units?.[0];
      const chapter = unit?.chapters?.[0];

      return (
        unit?.name &&
        chapter?.name &&
        duration?.toString().trim() !== "" &&
        durationType
      );
    }

    return false;
  };

  const generateVideoContent = async () => {
    try {
      const payload = {
        mail,
        board: user?.data?.board,
        clas: user?.data?.class,
        medium: medium,
        part: selectedPart,
        unit: selectedUnit,
        chapter: selectedSubject,
        lesson: selectedLesson,
        language: selectedLanguage,
      };
      const res = await post(API_GENERATE_VIDEO_TNPSC, payload);
      if (res) {
        if (res) {
          setPopupData({
            open: true,
            title: "Video Generation Started 🎬",
            description:
              "Your video is being generated. You’ll be notified once it's ready.",
            buttonText: "OK",
          });
        }
      }
    } catch (e) {
      console.log("ERROR COMES FROM GENERATE CONTENT VIDEO API ", e);
    }
  };

  const isValidateVideoContent =
    !selectedPart ||
    !selectedUnit ||
    !selectedSubject ||
    !selectedLesson ||
    !selectedLanguage;

  return {
    isFormValid,
    user,
    role,
    togglebtn,
    subjects,
    subjectOptions,
    selectedSubject,
    openDropdown,
    selectedLanguage,
    conceptText,
    languageRef,
    subjectRef,
    chapterRef,
    chapterOptions,
    preferredType,
    selectMode,
    selectedMedium,
    mediums,
    mediumRef,
    isLoading,
    chapters,
    duration,
    medium,
    isLoading,
    partRef,
    unitRef,
    lessonRef,
    isValidateVideoContent,
    setDuration,
    handleDurationChange,
    filteredDurationOptions,
    setSelectedSubject,
    setOpenDropdown,
    setSelectedLanguage,
    setConceptText,
    setSubjects,
    setPreferredType,
    setSelectMode,
    getLessonDetails,
    setSelectedMedium,
    getChapterDetails,
    onSelectChapter,
    onSelectSubject,
    onToggleBtn,
    addSubject,
    deleteSubject,
    addChapters,
    deleteChapter,
    addConcept,
    deleteConcept,
    aiGenerate,
    resetForm,
    aiLongGenerate,
    aiRandomGenerate,
    setSelectedPart,
    setDurationType,
    setDurationDropdown,
    durationDropdown,
    selectedUnit, // ✅ ADD
    setSelectedUnit, // ✅ ADD
    units,
    lessons,
    selectedChapter,
    setSelectedChapter,
    selectedPart,
    setSelectedLesson,
    selectedLesson,
    onSelectLesson,
    addUnit,
    deleteUnit,
    durationType,
    DURATION_OPTIONS,
    conceptOptions,
    getConceptDetails,
    navigate,
    popupData,
    setPopupData,
    handleExistingAssets,
    filteredAssets,
    setFilteredAssets,
    assetPopupOpen,
    setAssetPopupOpen,
    generateVideoContent,
    toggleNotify,
  };
};

export default useHandler;
